// frontend/src/components/QuantixGo/ExportErpPage.jsx

import React, { useState } from 'react';
import { quantixgoAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import ImageImportHistoryPanel from './ImageImportHistoryPanel';



const EXPORTS = [
  {
    key: 'products',
    labelKey: 'quantixgo.erp_export.products_button',
    labelFallback: 'Export Products',
    descKey: 'quantixgo.erp_export.products_description',
    descFallback: 'Pulls products flagged "Export to KORA" from Navision.',
    call: (api) => api.exportProductsFromErp(),
  },
  {
    key: 'product_variants',
    labelKey: 'quantixgo.erp_export.product_variants_button',
    labelFallback: 'Export Product Variants',
    descKey: 'quantixgo.erp_export.product_variants_description',
    descFallback: 'Pulls product variants (color/size) from Navision. Run after Products.',
    call: (api) => api.exportProductVariantsFromErp(),
  },  

  {
    key: 'salespersons',
    labelKey: 'quantixgo.erp_export.salespersons_button',
    labelFallback: 'Export Salespersons',
    descKey: 'quantixgo.erp_export.salespersons_description',
    descFallback: 'Pulls salespersons flagged "Export to KORA" from Navision.',
    call: (api) => api.exportSalespersonsFromErp(),
  },
  {
    key: 'sales_prices',
    labelKey: 'quantixgo.erp_export.sales_prices_button',
    labelFallback: 'Export Prices',
    descKey: 'quantixgo.erp_export.sales_prices_description',
    descFallback: 'Syncs price lists, then price rows, from Navision (in that order).',
    call: (api) => api.exportSalesPricesFromErp(),
  },
  {
    key: 'customers',
    labelKey: 'quantixgo.erp_export.customers_button',
    labelFallback: 'Export Customers',
    descKey: 'quantixgo.erp_export.customers_description',
    descFallback: 'Pulls customers flagged "Export to KORA" from Navision.',
    call: (api) => api.exportCustomersFromErp(),
  },
  {
    key: 'customer_salespersons',
    labelKey: 'quantixgo.erp_export.customer_salespersons_button',
    labelFallback: 'Export Customer Assignments',
    descKey: 'quantixgo.erp_export.customer_salespersons_description',
    descFallback: 'Syncs which salesperson(s) can see each customer. Run after Customers.',
    call: (api) => api.exportCustomerSalespersonsFromErp(),
  },  
  {
    key: 'delivery_addresses',
    labelKey: 'quantixgo.erp_export.delivery_addresses_button',
    labelFallback: 'Export Delivery Addresses',
    descKey: 'quantixgo.erp_export.delivery_addresses_description',
    descFallback: 'Syncs customer ship-to addresses. Run after Customers.',
    call: (api) => api.exportDeliveryAddressesFromErp(),
  },  
  {
  key: 'images',
  labelKey: 'quantixgo.erp_export.images_button',
  labelFallback: 'Import Product Images',
  descKey: 'quantixgo.erp_export.images_description',
  descFallback: 'Scans the image folder and syncs new/changed product images.',
  call: (api) => api.importProductImages(),
},
];

const IGNORED_KEYS = new Set(['batch_id']);

/**
 * Renders an arbitrary result object recursively. Handles both flat shapes
 * (products/salespersons: { upserted, rejected, errors, ... }) and nested
 * shapes (sales_prices: { priceLists: {...}, salesPrices: {...} }) without
 * needing to special-case either — new export endpoints can return whatever
 * shape makes sense without breaking this page.
 */
function ResultTree({ data, depth = 0 }) {
  if (data === null || data === undefined) return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    return (
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {data.map((item, idx) => (
          <li key={idx}>
            {typeof item === 'object' && item !== null
              ? (item.reason ? `#${item.index}: ${item.reason}` : JSON.stringify(item))
              : String(item)}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data).filter(([k]) => !IGNORED_KEYS.has(k));
    return (
      <ul style={{ margin: 0, paddingLeft: depth === 0 ? '18px' : '14px' }}>
        {entries.map(([k, v]) => {
          const isComplex = v !== null && typeof v === 'object';
          return (
            <li key={k}>
              {isComplex ? (
                <>
                  <strong>{k}</strong>
                  <ResultTree data={v} depth={depth + 1} />
                </>
              ) : (
                <>{k}: {String(v)}</>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return <span>{String(data)}</span>;
}



const ExportErpPage = () => {
  const { t } = useTranslation();
  const { toast_success, toast_error } = useToast();

  const [loadingKey, setLoadingKey] = useState(null);
  const [results, setResults] = useState({}); // { [key]: lastResultData }

  const handleExport = async (item) => {
    setLoadingKey(item.key);
    try {
      const response = await item.call(quantixgoAPI);
      const data = response.data?.data;
      setResults((prev) => ({ ...prev, [item.key]: data }));
      toast_success(t(item.labelKey, item.labelFallback));
    } catch (err) {
      console.error(`Export '${item.key}' failed:`, err);
      toast_error(err.response?.data?.error || `${t(item.labelKey, item.labelFallback)} failed`);
    } finally {
      setLoadingKey(null);
    }
  };

  const [showImageHistory, setShowImageHistory] = useState(false);

  return (
    <div className="page-container">
      <h1>{t('quantixgo.erp_export.title', 'Export ERP')}</h1>
      <p>{t('quantixgo.erp_export.subtitle', 'Trigger a manual export from Navision into QuantixGo.')}</p>

      <div className="export-erp-grid" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
        {EXPORTS.map((item) => (
          <div key={item.key} className="export-erp-card" style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '16px' }}>
            <h3>{t(item.labelKey, item.labelFallback)}</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>{t(item.descKey, item.descFallback)}</p>

            <button
              className="btn btn-primary"
              onClick={() => handleExport(item)}
              disabled={loadingKey !== null}
            >
              {loadingKey === item.key
                ? t('quantixgo.erp_export.processing', 'Processing...')
                : t(item.labelKey, item.labelFallback)}
            </button>

            {item.key === 'images' && (
              <>
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: '8px' }}
                  onClick={() => setShowImageHistory((v) => !v)}
                >
                  {showImageHistory
                    ? t('quantixgo.erp_export.hideHistory', 'Hide History')
                    : t('quantixgo.erp_export.showHistory', 'History')}
                </button>
                <ImageImportHistoryPanel visible={showImageHistory} />
              </>
            )}

            {results[item.key] && (
              <div style={{ marginTop: '12px' }}>
                <ResultTree data={results[item.key]} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExportErpPage;